import type { ReactNode } from "react";

import styles from "./EmptyState.module.css";
import { MeshMark } from "./MeshMark";

interface EmptyStateProps {
  title: string;
  /** What is so, and what to do — an instruction, not an apology. */
  children?: ReactNode;
  /** The ways on: the one that leads on first. */
  actions?: ReactNode;
  /**
   * Whether the state announces itself (`role="status"`). Off where a
   * caller already puts the focus on it under its own name — a region
   * mounted together with its text is not reliably read out anyway.
   */
  live?: boolean;
}

/**
 * A screen with nothing to show but a way on (UX gate round 4, #11): a
 * Vorgang that is gone, a board or a calculation without a Stand. Until now
 * a grey notice at the top of an empty page — it read like a message that
 * something had gone wrong, with 600 px of nothing under it. Now the empty
 * state every list has (`MeshMark`, grey, never red — the register's „Leg
 * einen Vorgang an."): a panel of its own, the mesh beside the words, the
 * whole of it in the middle.
 */
export function EmptyState({ title, children, actions, live = true }: EmptyStateProps) {
  return (
    <section className={styles.empty} role={live ? "status" : undefined}>
      <div className={styles.inner}>
        <span className={styles.mark}>
          <MeshMark size={48} />
        </span>
        <div className={styles.text}>
          <p className={styles.title}>{title}</p>
          {children ? <div className={styles.body}>{children}</div> : null}
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
      </div>
    </section>
  );
}
